// src/pages/PolitiqueConfidentialite.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';


function PolitiqueConfidentialite({ theme, toggleTheme }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-slate dark:prose-invert">
        {/* Header */}
        <header className="flex justify-between items-center mb-12">
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-2"
          >
            ← Retour
          </button>
          <button
            onClick={toggleTheme}
            className="px-5 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-md"
          >
            {theme === 'light' ? 'Mode Sombre' : 'Mode Clair'}
          </button>
        </header>

        <h1 className="text-4xl font-bold mb-10 text-gray-900 dark:text-white">
          Politique de Confidentialité
        </h1>

        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Dernière mise à jour : 17 janvier 2026
        </p>

        <p className="mb-8 leading-relaxed text-gray-800 dark:text-gray-200">
          Nous, développeurs et gestionnaires de ce projet personnel <strong>suivi-de-portefeuille.fr</strong>, 
          nous engageons à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données 
          (RGPD – Règlement UE 2016/679).
        </p>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          1. Contact
        </h2>
        <p className="mb-6">
          Contact :{' '}
          <a
            href="mailto:contact@suivi-de-portefeuille.fr"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            contact@suivi-de-portefeuille.fr
          </a>
        </p>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          2. Données collectées
        </h2>
        <p className="mb-4">
          Nous collectons uniquement les données strictement nécessaires au fonctionnement du service :
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Données d’inscription : adresse e-mail, mot de passe (haché)</li>
          <li>
            Données de portefeuille saisies par vos soins : tickers, quantités, prix d’achat, dates
          </li>
          <li>Données techniques : adresse IP, type de navigateur, horodatages de connexion (logs)</li>
          <li>
            <strong>Avec abonnement Premium :</strong> informations relatives à l’abonnement
            (identifiant client Stripe, statut, date de début/fin)
          </li>
        </ul>
        <p className="font-medium mb-2">Nous ne collectons jamais :</p>
        <ul className="list-disc pl-6 mb-6 space-y-1">
          <li>Vos coordonnées bancaires complètes</li>
          <li>Numéro de carte bancaire (traité exclusivement par Stripe)</li>
          <li>Données sensibles</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          3. Finalités du traitement
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Création et gestion de votre compte + suivi de votre portefeuille</li>
          <li>Gestion des abonnements Premium et facturation</li>
          <li>Sécurité du service (prévention fraude, détection connexions inhabituelles)</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          4. Base légale
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>
            Exécution du contrat (art. 6.1.b RGPD) → compte + utilisation du service + abonnement
          </li>
          <li>Intérêt légitime (art. 6.1.f RGPD) → sécurité du site</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          5. Destinataires & sous-traitants
        </h2>
        <p className="mb-4">
          Vos données sont traitées de manière confidentielle et ne sont accessibles qu’au gestionnaire du site.
        </p>
        <p className="mb-4">
          <strong>Sous-traitant principal pour les paiements :</strong>
          <br />
          Stripe Payments Europe Ltd (Irlande) et Stripe Inc. (États-Unis) agissent en qualité de
          sous-traitants pour la gestion des paiements et abonnements.
        </p>
        <p className="mb-6">
          Les données de paiement (numéro de carte, etc.) <strong>ne transitent jamais par nos
          serveurs</strong> – elles sont traitées directement et sécurisées par Stripe (conforme
          PCI-DSS niveau 1).
          <br />
          Transfert vers les États-Unis encadré par les Clauses Contractuelles Types (CCT) adoptées
          par la Commission européenne + mesures complémentaires mises en place par Stripe.
        </p>
        <p className="mb-6">
          Politique de confidentialité Stripe :{' '}
          <a
            href="https://stripe.com/fr/privacy"
            className="underline text-blue-600 hover:text-blue-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://stripe.com/fr/privacy
          </a>
        </p>
        <p>Aucun partage avec des tiers à des fins publicitaires ou marketing.</p>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          6. Durée de conservation
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Compte actif → tant que vous ne supprimez pas votre compte</li>
          <li>Données de portefeuille → jusqu’à suppression du compte</li>
          <li>Logs de connexion → 12 mois maximum</li>
          <li>
            Données liées à l’abonnement → durée de l’abonnement + 10 ans (obligation légale
            facturation France)
          </li>
          <li>
            Suppression compte → effacement quasi-total sous 30 jours (sauf obligation légale)
          </li>
        </ul>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          7. Vos droits RGPD
        </h2>
        <p className="mb-4">
          Vous pouvez à tout moment (gratuitement, sauf demande manifestement abusive) :
        </p>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Accéder à vos données</li>
          <li>Les rectifier</li>
          <li>Demander leur effacement</li>
          <li>Demander la portabilité (format JSON/CSV)</li>
          <li>Vous opposer à certains traitements</li>
        </ul>
        <p className="mb-6">
          Il suffit de nous écrire à :{' '}
          <a
            href="mailto:contact@suivi-de-portefeuille.fr"
            className="underline text-blue-600 hover:text-blue-800"
          >
            contact@suivi-de-portefeuille.fr
          </a>
        </p>
        <p>
          Réponse sous 1 mois (prolongeable 2 mois si complexe). 
        </p>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          8. Sécurité
        </h2>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Mots de passe hachés (bcrypt)</li>
          <li>Connexions en HTTPS partout</li>
          <li>Accès ultra-restreint</li>
          <li>Hébergement sécurisé (Vercel + Stripe pour les paiements)</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          9. Cookies & traceurs
        </h2>
        <p>
          Ce site n’utilise pour l’instant que des cookies techniques indispensables (pas
          d’analytics tiers ni de publicité).
        </p>

        <h2 className="text-2xl font-semibold mt-12 mb-6 text-gray-900 dark:text-white">
          10. Modifications de la politique
        </h2>
        <p className="mb-8">
          Toute modification importante sera signalée (notification sur le site ou par email).
        </p>

        <p className="italic text-gray-600 dark:text-gray-400 mt-16">
          Merci de votre confiance pour suivre vos investissements.
          <br />
          N’hésitez pas à nous contacter pour toute question !
        </p>
      </div>
    </div>
  );
}

export default PolitiqueConfidentialite;